import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../components/common/PageHeader";
import Select from "react-select";
import { toast } from "react-toastify";

import {
    getUserProfileById,
    getEmployeeVerticalLocationByUserId,
    getEmployeeDesignationByUserId,
    getEmployeeDepartmentByDesignationId,
    updateEmployeeProfile,
    assignEmployeeVerticalLocation,
    assignEmployeeDepartment,
    assignEmployeeDesignation,
    getVerticals,
    getLocationsByVertical,
    getDepartments,
    getRoles,
    getDesignations,
    getReportingManagers,
    getCompanyUserById,
    getHRRoles,
    assignRolesToUser,
    updateCompanyUser,
} from "../api/hrService";
import api from "../../../api/api";
import Swal from "sweetalert2";

/* =========================
   TYPES
========================= */
interface HRUserForm {
    verticalLocations: {
        vertical_id: number;
        location_ids: number[];
    }[];

    vertical: number | null;

    location: number[];
    firstName: string;
    middleName: string;
    lastName: string;
    contactNo: string;
    dob: string;
    gender: string;
    aadhaar: string;
    pan: string;
    currentAddress: string;
    permanentAddress: string;
    personalEmail: string;
    officeEmail: string;
    joiningDate: string;
    leavingDate: string | null;
    status: string;

    department: number | null;
    roles: number[];       // ids
    roleNames: string[];   // names for API
    designation: number | null;
    reportingManager: number | null;
    salesTarget: string;

    username: string;
    password: string;
    confirmPassword: string;
}

const EditHRUser: React.FC = () => {
    const { id } = useParams();
    const userId = Number(id);
    const navigate = useNavigate();

    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [verticalOptions, setVerticalOptions] = useState<any[]>([]);
    const [locationOptions, setLocationOptions] = useState<any[]>([]);
    const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
    const [roleOptions, setRoleOptions] = useState<any[]>([]);
    const [designationOptions, setDesignationOptions] = useState<any[]>([]);
    const [managerOptions, setManagerOptions] = useState<any[]>([]);
    const [loadingLocations, setLoadingLocations] = useState(false);

    const [employeeId, setEmployeeId] = useState<number | null>(null);
    const [employeeDepartmentMappingId, setEmployeeDepartmentMappingId] = useState<number | null>(null);
    const [originalLocations, setOriginalLocations] = useState<number[]>([]);
    const [originalStatus, setOriginalStatus] = useState("");
    const [originalRole, setOriginalRole] = useState("");
    const [formData, setFormData] = useState<HRUserForm>({
        vertical: null,
        location: [],
        verticalLocations: [],
        firstName: "",
        middleName: "",
        lastName: "",
        contactNo: "",
        dob: "",
        gender: "",
        aadhaar: "",
        pan: "",
        currentAddress: "",
        permanentAddress: "",
        personalEmail: "",
        officeEmail: "",
        joiningDate: "",
        leavingDate: null,
        status: "",

        department: null,
        roles: [],
        roleNames: [],
        designation: null,
        reportingManager: null,
        salesTarget: "",

        username: "",
        password: "",
        confirmPassword: "",
    });
    const isNonVerticalRole = (roleName?: string) =>
        ["admin", "hr"].includes((roleName || "").toLowerCase());

    const isAdminRole = (roleName?: string) =>
        (roleName || "").toLowerCase() === "admin";




    /* =========================
       MASTER DATA
    ========================= */
    useEffect(() => {
        Promise.all([
            getVerticals(),
            getDepartments(),
            getHRRoles(),
            getDesignations(),
        ]).then(([v, d, r, des]) => {
            setVerticalOptions(v.data.results.map((i: any) => ({ value: i.id, label: i.name })));
            setDepartmentOptions(d.data.results.map((i: any) => ({ value: i.id, label: i.name })));
            setRoleOptions(
                r.data.data.map((i: any) => ({
                    value: i.id,
                    label: i.name,
                    name: i.name,     // ⭐ important
                }))
            );
            setDesignationOptions(des.data.results.map((i: any) => ({ value: i.id, label: i.name })));
        });
    }, []);

    /* =========================
       HELPERS
    ========================= */
    const fetchLocationsFromVerticals = async (verticalIds: number[]) => {
        if (!verticalIds.length) return;

        setLoadingLocations(true);
        try {
            let all: any[] = [];
            for (const v of verticalIds) {
                if (!v || Number.isNaN(v)) continue;
                const res = await getLocationsByVertical(v);
                all.push(...res.data.results.map((l: any) => ({
                    value: l.id,
                    label: l.name,
                })));
            }
            setLocationOptions(Array.from(new Map(all.map(i => [i.value, i])).values()));
        } finally {
            setLoadingLocations(false);
        }
    };


    const handleReportingManagerLoad = async () => {

        const role = formData.roleNames?.[0]?.toLowerCase();

        /* ================= CASE 1 : ADMIN ================= */
        if (role === "admin") {
            setManagerOptions([]);        // hide
            return;                       // ❌ no API call
        }

        /* ================= CASE 2 : HR ================= */
        if (role === "hr") {
            try {
                const res = await getReportingManagers({
                    role: "Hr"
                });

                const managers = res?.data?.results || [];

                setManagerOptions(
                    managers.map((m: any) => ({
                        value: m.id,
                        label: m.full_name || m.email
                    }))
                );

            } catch {
                setManagerOptions([]);
            }
            return;
        }

        /* ================= CASE 3 : OTHER ROLES ================= */

        // if (!formData.vertical || !formData.location.length) {

        //     toast.info("Please select Vertical and Location first");

        //     setTimeout(() => setActiveStep(0), 0);

        //     setManagerOptions([]);
        //     return;
        // }

        if (!formData.vertical) {
            toast.info("Please select Vertical first");
            setTimeout(() => setActiveStep(0), 0);
            setManagerOptions([]);
            return;
        }

        if (!formData.location.length) {
            toast.info("Please select Location first");
            setTimeout(() => setActiveStep(0), 0);
            setManagerOptions([]);
            return;
        }

        try {
            const res = await getReportingManagers({
                vertical_ids: [formData.vertical],
                role: formData.roleNames?.[0],
                location_ids: formData.location,
            });

            const managers = res?.data?.results || [];

            setManagerOptions(
                managers.map((m: any) => ({
                    value: m.id,
                    label: m.full_name || m.email
                }))
            );

        } catch {
            setManagerOptions([]);
        }
    };

    useEffect(() => {
        if (activeStep !== 1) return;
        if (!formData.roleNames?.length) return;

        // wait until user data loaded
        if (loading) return;

        handleReportingManagerLoad();
    }, [activeStep, formData.roleNames, formData.vertical, formData.location, loading]);


    useEffect(() => {
        if (!managerOptions.length || !formData.reportingManager) return;

        const exists = managerOptions.some(
            m => m.value === formData.reportingManager
        );

        if (!exists) return;

        // force re-render so select shows value
        setFormData(prev => ({
            ...prev,
            reportingManager: prev.reportingManager
        }));
    }, [managerOptions]);

    /* =========================
       LOAD USER
    ========================= */
    useEffect(() => {
        if (!userId) return;

        const loadUser = async () => {
            try {
                /* PROFILE */
                const profileRes = await getUserProfileById(userId);
                const p = profileRes.data?.data;

                console.log(p.id);
                if (!p?.id) {
                    toast.error("Employee profile id not found");
                    return;
                }

                setEmployeeId(p.id);

                setFormData(prev => ({
                    ...prev,
                    firstName: p.first_name || "",
                    middleName: p.middle_name || "",
                    lastName: p.last_name || "",
                    contactNo: p.mobile || "",
                    dob: p.dob || "",
                    gender: p.gender_type || "",
                    aadhaar: p.aadhar_no || "",
                    pan: p.pan_no || "",
                    currentAddress: p.current_address || "",
                    permanentAddress: p.permanent_address || "",
                    personalEmail: p.personal_email_id || "",
                    officeEmail: p.official_mail_id || "",
                    joiningDate: p.joining_date || "",
                    leavingDate: p.leaving_date,
                    status: p.is_active ? "Active" : "Inactive",
                    username: String(p.user || ""),
                }));
                const rolesFromProfile = Array.isArray(p.role || p.roles)
                    ? (p.role || p.roles)
                    : [p.role || p.roles].filter(Boolean);

                setFormData(prev => ({
                    ...prev,
                    roleNames: rolesFromProfile,
                }));




                try {
                    const userRes = await getCompanyUserById(userId);

                    setFormData(prev => ({
                        ...prev,
                        username: userRes.data?.username || "",
                        officeEmail: userRes.data?.email || "",
                    }));

                } catch (e) {
                    console.error("Failed to fetch username", e);
                    toast.error("Failed to load login details");
                }

                /* VERTICAL + LOCATION */
                const vlRes = await getEmployeeVerticalLocationByUserId(userId);

                const map = new Map<number, number[]>();

                vlRes.data.results.forEach((i: any) => {
                    if (!map.has(i.vertical)) map.set(i.vertical, []);
                    map.get(i.vertical)!.push(i.location);
                });

                const verticalLocations = Array.from(map.entries()).map(
                    ([vertical_id, location_ids]) => ({
                        vertical_id,
                        location_ids,
                    })
                );

                // 🔥 SET BOTH: real data + UI helpers
                setFormData(prev => ({
                    ...prev,
                    verticalLocations,
                    vertical: verticalLocations[0]?.vertical_id || null,
                    location: verticalLocations[0]?.location_ids || [],
                }));

                setOriginalLocations(
                    verticalLocations[0]?.location_ids || []
                );

                setOriginalStatus(
                    p.is_active ? "Active" : "Inactive"
                );

                setOriginalRole(
                    String(rolesFromProfile?.[0] || "")
                );



                await fetchLocationsFromVerticals(
                    verticalLocations[0]?.vertical_id
                        ? [verticalLocations[0].vertical_id]
                        : []
                );


                /* DESIGNATION */
                const desRes = await getEmployeeDesignationByUserId(userId);

                const d = desRes.data.results?.find(
                    (i: any) => i.designation_id?.id
                );

                if (d) {
                    setFormData(prev => ({
                        ...prev,
                        designation: d.designation_id?.id || null,
                        reportingManager: d.assign_reporting?.id || null,
                        salesTarget: d.sales_target || "",
                    }));

                    // ✅ CORRECT: department by USER ID
                    const deptMapRes = await api.get(
                        `/hr/v1/employee-department/?user_id=${userId}`
                    );

                    const deptMapping = deptMapRes.data?.results?.[0];

                    setEmployeeDepartmentMappingId(deptMapping?.id || null);

                    setFormData(prev => ({
                        ...prev,
                        department: deptMapping?.department_id || null,
                    }));


                }

            } catch {
                toast.error("Failed to load employee 11");
            } finally {
                setLoading(false);
            }
        };

        loadUser();
    }, [userId]);

    useEffect(() => {
        if (!roleOptions.length || !formData.roleNames?.length) return;

        const matchedIds = roleOptions
            .filter(o =>
                formData.roleNames.includes(o.name)
            )
            .map(o => o.value);

        setFormData(prev => ({
            ...prev,
            roles: matchedIds,
        }));
    }, [roleOptions, formData.roleNames]);


    /* =========================
       HANDLERS
    ========================= */
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSubmit = async () => {
        if (!employeeId) return;
        /* ✅ GENDER VALIDATION */
        if (!formData.gender) {
            setErrors(prev => ({
                ...prev,
                gender: "Gender is required",
            }));

            setActiveStep(0);   // go back to Personal Details step
            toast.error("Please select Gender");
            return;
        }

        setSubmitting(true);

        /* ================= LOGIN VALIDATION ================= */
        if (formData.password || formData.confirmPassword) {

            if (formData.password !== formData.confirmPassword) {
                toast.error("Passwords do not match");
                setSubmitting(false);
                return;
            }

            if (formData.password.length < 6) {
                toast.error("Password must be at least 6 characters");
                setSubmitting(false);
                return;
            }
        }
        const isLocationChanged =
            JSON.stringify([...formData.location].sort()) !==
            JSON.stringify([...originalLocations].sort());

        const shouldShowEnquiryValidation =
            isLocationChanged ||
            originalStatus !== formData.status;
        try {



            await updateEmployeeProfile(userId, {
                first_name: formData.firstName,
                middle_name: formData.middleName,
                last_name: formData.lastName,
                mobile: formData.contactNo,
                dob: formData.dob,
                gender_type: formData.gender,
                aadhar_no: formData.aadhaar,
                pan_no: formData.pan,
                current_address: formData.currentAddress,
                permanent_address: formData.permanentAddress,
                personal_email_id: formData.personalEmail,   // ✅ ADD
                official_mail_id: formData.officeEmail,      // ✅ ADD
                joining_date: formData.joiningDate,
                leaving_date: formData.leavingDate,
                is_active: formData.status,
            });
            const loginPayload: any = {
                user_role: [],
                user_permissions: [],
                username: formData.username,   // ✅ ADD THIS
            };

            if (formData.officeEmail) {
                loginPayload.email = formData.officeEmail;
            }

            if (formData.password) {
                loginPayload.password = formData.password;
                loginPayload.confirm_password = formData.confirmPassword;
            }

            await updateCompanyUser(userId, loginPayload);




            const promises: Promise<any>[] = [];
            if (formData.department) {
                promises.push(
                    api.put(`/hr/v1/employee-department/${employeeId}/`, {
                        employee_id: employeeId,
                        department_id: formData.department,
                        vertical_ids: formData.vertical
                            ? [formData.vertical]
                            : [],
                    })
                );
            }



            const selectedRoleName = formData.roleNames?.[0];
            const skipVertical = isNonVerticalRole(selectedRoleName);
            const isAdmin = isAdminRole(selectedRoleName);
            if (isLocationChanged || skipVertical) {

                if (skipVertical) {
                    await assignEmployeeVerticalLocation({
                        employee_id: employeeId,
                        vertical_locations: [],
                    });

                } else if (formData.vertical) {

                    await assignEmployeeVerticalLocation({
                        employee_id: employeeId,
                        vertical_locations: [
                            {
                                vertical_id: formData.vertical,
                                location_ids: formData.location,
                            },
                        ],
                    });
                }
            }
            const designationPayload: any = {
                employee_id: employeeId,
                designation_id: formData.designation,
                sales_target: formData.salesTarget,
                assign_reporting: isAdmin ? null : formData.reportingManager,
            };


            if (!isAdmin) {
                designationPayload.assign_reporting = formData.reportingManager;
            }

            promises.push(assignEmployeeDesignation(designationPayload));


            await Promise.all(promises);

            await assignRolesToUser({
                user_id: userId,
                role: formData.roleNames?.[0] || "",
            });



            toast.success("Employee updated successfully");
            navigate("/hr/users");
        } catch (error: any) {

            const apiData = error?.response?.data;
            console.log("apiData", apiData);
            // ✅ CASE: Assigned enquiries present
            if (
                shouldShowEnquiryValidation &&
                apiData?.message?.toLowerCase().includes("assigned enquiries") &&
                apiData?.enquiries?.length
            ) {

                const enquiryRows = apiData.enquiries
                    .map((e: any, index: number) => `
            <tr>
                <td style="padding:8px; border:1px solid #ddd;">
                    ${index + 1}
                </td>

                <td style="padding:8px; border:1px solid #ddd;">
                    ${e.id || "-"}
                </td>

                <td style="padding:8px; border:1px solid #ddd;">
                    ${e.enquiry_title || "-"}
                </td>

                <td style="padding:8px; border:1px solid #ddd;">
                    ${e.customer_id__name || "-"}
                </td>

                <td style="padding:8px; border:1px solid #ddd;">
                    ${e.vertical__name || "-"}
                </td>

                <td style="padding:8px; border:1px solid #ddd;">
                    ${e.city || "-"}
                </td>

                <td style="padding:8px; border:1px solid #ddd;">
                    ${e.enquiry_stage__stage_name || "-"}
                </td>
            </tr>
        `)
                    .join("");

                Swal.fire({
                    icon: "warning",
                    title: "User has assigned enquiries",
                    width: "1000px",
                    confirmButtonColor: "#2c394a",
                    html: `
            <div style="text-align:left;">
                <p style="margin-bottom:15px;">
                    Please reassign below enquiries first:
                </p>

                <div style="max-height:400px; overflow:auto;">
                    <table style="
                        width:100%;
                        border-collapse:collapse;
                        font-size:14px;
                    ">
                        <thead style="background:#f5f5f5; position:sticky; top:0;">
                            <tr>
                                <th style="padding:10px; border:1px solid #ddd;">#</th>
                                <th style="padding:10px; border:1px solid #ddd;">ID</th>
                                <th style="padding:10px; border:1px solid #ddd;">Project</th>
                                <th style="padding:10px; border:1px solid #ddd;">Customer</th>
                                <th style="padding:10px; border:1px solid #ddd;">Vertical</th>
                                <th style="padding:10px; border:1px solid #ddd;">City</th>
                                <th style="padding:10px; border:1px solid #ddd;">Stage</th>
                            </tr>
                        </thead>

                        <tbody>
                            ${enquiryRows}
                        </tbody>
                    </table>
                </div>
            </div>
        `,
                    confirmButtonText: "OK",
                });

                return;
            }

            // ❌ fallback
            toast.error(apiData?.message || "Update failed");
        } finally {
            setSubmitting(false);
        }
    };

    const statusOptions = [
        { value: "Active", label: "Active" },
        { value: "Inactive", label: "Inactive" },
    ];

    useEffect(() => {
        if (!departmentOptions.length || !formData.department) return;

        const exists = departmentOptions.some(
            opt => opt.value === formData.department
        );

        if (!exists) {
            setFormData(prev => ({
                ...prev,
                department: null,
            }));
        }
    }, [departmentOptions]);
    if (loading) {
        return <div className="p-5 text-center">Loading employee...</div>;
    }
    const selectedVerticalOption =
        verticalOptions.find(opt => opt.value === formData.vertical) || null;


    const selectedLocationOptions = locationOptions.filter(opt =>
        formData.location.includes(opt.value)
    );
    // Department
    const selectedDepartmentOption =
        departmentOptions.find(opt => opt.value === formData.department) || null;




    // Designation
    const selectedDesignationOption =
        designationOptions.find(opt => opt.value === formData.designation) || null;

    // Reporting Manager
    const selectedReportingManagerOption =
        managerOptions.find(opt => opt.value === formData.reportingManager) || null;
    const selectedRoleName = formData.roleNames?.[0];
    const isAdmin = isAdminRole(selectedRoleName);

    /* =========================
       UI
    ========================= */
    return (
        <>
            <PageHeader
                title="Edit Employee"
                breadcrumbs={[
                    { label: "Dashboard", path: "/dashboard" },
                    { label: "HR" },
                    { label: "Employee Master", path: "/hr/users" },
                    { label: "Edit Employee" },
                ]}
            />
            <div className="bg-gray">
                <div className="container pt-4 pb-4">
                    <div className="d-flex mb-4">
                        {["Personal Details", "Professional Details", "Login Details"].map(
                            (step, index) => (
                                <div
                                    key={step}
                                    className={`flex-fill text-center p-2 border ${index === activeStep
                                        ? "bg-primary text-white fw-bold"
                                        : "bg-white"
                                        }`}
                                >
                                    {step}
                                </div>
                            )
                        )}
                    </div>

                    <div className="card p-4">

                        {/* ================= STEP 0 – PERSONAL DETAILS ================= */}
                        {activeStep === 0 && (
                            <div className="row g-4">

                                {/* Vertical */}
                                <div className="col-md-3">
                                    <label className="form-label fw-semibold">Vertical *</label>
                                    {/* <Select
                                        options={verticalOptions}
                                        value={
                                            verticalOptions.find(o => o.value === formData.vertical) || null
                                        }
                                        onChange={(selected) => {
                                            const value = selected?.value || null;

                                            setFormData(prev => ({
                                                ...prev,
                                                vertical: value,
                                                location: [],
                                                verticalLocations: value
                                                    ? [{
                                                        vertical_id: value,
                                                        location_ids: [],
                                                    }]
                                                    : [],
                                            }));

                                            fetchLocationsFromVerticals(value ? [value] : []);
                                        }}
                                    /> */}
                                    <Select
                                        options={verticalOptions}
                                        value={
                                            verticalOptions.find(o => o.value === formData.vertical) || null
                                        }
                                        isDisabled
                                    />


                                    {errors.vertical && (
                                        <small className="text-danger">{errors.vertical}</small>
                                    )}
                                </div>

                                {/* Location */}
                                <div className="col-md-3">
                                    <label className="form-label fw-semibold">Location *</label>
                                    <Select
                                        isMulti
                                        options={locationOptions}
                                        value={locationOptions.filter(o =>
                                            formData.location.includes(o.value)
                                        )}
                                        onChange={(selected) => {
                                            const locIds = selected.map(s => s.value);

                                            setFormData(prev => ({
                                                ...prev,
                                                location: locIds,
                                                verticalLocations: prev.vertical
                                                    ? [{
                                                        vertical_id: prev.vertical,
                                                        location_ids: locIds,
                                                    }]
                                                    : [],
                                            }));
                                        }}
                                        isDisabled={!formData.vertical}
                                    />




                                    {errors.location && (
                                        <small className="text-danger">{errors.location}</small>
                                    )}
                                </div>

                                <div className="col-md-6" />

                                {/* Name */}
                                <div className="col-md-3">
                                    <input
                                        className={`form-control ${errors.firstName && "is-invalid"}`}
                                        name="firstName"
                                        placeholder="First Name *"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                    />
                                    {errors.firstName && (
                                        <div className="invalid-feedback">{errors.firstName}</div>
                                    )}
                                </div>

                                <div className="col-md-3">
                                    <input
                                        className="form-control"
                                        name="middleName"
                                        placeholder="Middle Name"
                                        value={formData.middleName}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <input
                                        className={`form-control ${errors.lastName && "is-invalid"}`}
                                        name="lastName"
                                        placeholder="Last Name *"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                    />
                                    {errors.lastName && (
                                        <div className="invalid-feedback">{errors.lastName}</div>
                                    )}
                                </div>

                                {/* Contact */}
                                <div className="col-md-3">
                                    <input
                                        className={`form-control ${errors.contactNo && "is-invalid"}`}
                                        name="contactNo"
                                        placeholder="Contact No *"
                                        value={formData.contactNo}
                                        onChange={handleChange}
                                    />
                                    {errors.contactNo && (
                                        <div className="invalid-feedback">{errors.contactNo}</div>
                                    )}
                                </div>

                                {/* Emails */}
                                <div className="col-md-3">
                                    <input
                                        className={`form-control ${errors.personalEmail && "is-invalid"}`}
                                        name="personalEmail"
                                        placeholder="Personal Email"
                                        value={formData.personalEmail}
                                        onChange={handleChange}
                                    />
                                    {errors.personalEmail && (
                                        <div className="invalid-feedback">{errors.personalEmail}</div>
                                    )}
                                </div>

                                <div className="col-md-3">
                                    <input
                                        className={`form-control ${errors.officeEmail && "is-invalid"}`}
                                        name="officeEmail"
                                        placeholder="Office Email"
                                        value={formData.officeEmail}
                                        onChange={handleChange}
                                    />
                                    {errors.officeEmail && (
                                        <div className="invalid-feedback">{errors.officeEmail}</div>
                                    )}
                                </div>

                                {/* DOB & Gender */}
                                <div className="col-md-3">
                                    <input
                                        type="date"
                                        className={`form-control ${errors.dob && "is-invalid"}`}
                                        name="dob"
                                        value={formData.dob}
                                        onChange={handleChange}
                                    />
                                    {errors.dob && (
                                        <div className="invalid-feedback">{errors.dob}</div>
                                    )}
                                </div>

                                <div className="col-md-3">
                                    <select
                                        className={`form-control ${errors.gender && "is-invalid"}`}
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Gender</option>
                                        <option>Male</option>
                                        <option>Female</option>
                                        <option>Other</option>
                                    </select>
                                    {errors.gender && (
                                        <small className="text-danger">{errors.gender}</small>
                                    )}
                                </div>

                                {/* Aadhaar / PAN */}
                                <div className="col-md-3">
                                    <input
                                        className="form-control"
                                        name="aadhaar"
                                        placeholder="Aadhaar *"
                                        value={formData.aadhaar}
                                        onChange={handleChange}
                                    />
                                    {errors.aadhaar && (
                                        <small className="text-danger">{errors.aadhaar}</small>
                                    )}
                                </div>

                                <div className="col-md-3">
                                    <input
                                        className="form-control"
                                        name="pan"
                                        placeholder="PAN"
                                        value={formData.pan}
                                        onChange={handleChange}
                                    />
                                    {errors.pan && (
                                        <small className="text-danger">{errors.pan}</small>
                                    )}
                                </div>

                                {/* Address */}
                                <div className="col-md-3">
                                    <input
                                        className={`form-control ${errors.currentAddress && "is-invalid"}`}
                                        name="currentAddress"
                                        placeholder="Current Address *"
                                        value={formData.currentAddress}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <input
                                        className={`form-control ${errors.permanentAddress && "is-invalid"}`}
                                        name="permanentAddress"
                                        placeholder="Permanent Address *"
                                        value={formData.permanentAddress}
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* Joining / Leaving */}
                                <div className="col-md-3">
                                    <input
                                        type="date"
                                        className={`form-control ${errors.joiningDate && "is-invalid"}`}
                                        name="joiningDate"
                                        value={formData.joiningDate}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <Select
                                        options={statusOptions}
                                        value={
                                            statusOptions.find(opt => opt.value === formData.status) || null
                                        }
                                        onChange={(selected) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                status: selected?.value || "",
                                                ...(selected?.value === "Active"
                                                    ? { leavingDate: null }
                                                    : {}),
                                            }));
                                        }}

                                        placeholder="Status *"
                                        classNamePrefix="react-select"
                                        className={errors.status ? "is-invalid-select" : ""}
                                    />

                                    {errors.status && (
                                        <small className="text-danger">{errors.status}</small>
                                    )}
                                </div>



                                {formData.status === "Inactive" && (
                                    <div className="col-md-3">
                                        <input
                                            type="date"
                                            className={`form-control ${errors.leavingDate ? "is-invalid" : ""}`}
                                            name="leavingDate"
                                            value={formData.leavingDate || ""}
                                            onChange={handleChange}
                                        />

                                        {errors.leavingDate && (
                                            <small className="text-danger">{errors.leavingDate}</small>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ================= STEP 1 – PROFESSIONAL DETAILS ================= */}
                        {activeStep === 1 && (
                            <div className="row g-4">
                                <div className="col-md-6">
                                    <Select
                                        options={departmentOptions}
                                        value={selectedDepartmentOption}
                                        onChange={(selected) =>
                                            setFormData(prev => ({
                                                ...prev,
                                                department: selected?.value || null,
                                            }))
                                        }
                                        placeholder="Select Department"
                                        classNamePrefix="react-select"
                                    />



                                    {errors.department && (
                                        <small className="text-danger">{errors.department}</small>
                                    )}
                                </div>


                                <div className="col-md-6">
                                    <Select
                                        isMulti
                                        options={roleOptions}
                                        value={roleOptions.filter(o =>
                                            formData.roles.includes(o.value)
                                        )}
                                        onChange={(selected) => {
                                            const last = selected.slice(-1);
                                            const roleName = last[0]?.name;
                                            const nonVertical = isNonVerticalRole(roleName);
                                            const isAdmin = isAdminRole(roleName);
                                            if (isAdmin) {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    reportingManager: null,
                                                }));
                                            }
                                            setFormData(prev => ({
                                                ...prev,
                                                roles: last.map(s => s.value),
                                                roleNames: last.map(s => s.name),

                                                ...(nonVertical && {
                                                    vertical: null,
                                                    location: [],
                                                    verticalLocations: [],
                                                    reportingManager: null
                                                }),
                                            }));
                                            if (isAdmin) {
                                                Swal.fire({
                                                    icon: "info",
                                                    title: "Reporting manager not required",
                                                    text: "Admin users do not report to anyone.",
                                                    confirmButtonColor: "#3085d6",
                                                });
                                            }
                                            // ⭐ Swal same as Create
                                            if (nonVertical) {
                                                Swal.fire({
                                                    icon: "info",
                                                    title: "Vertical not required",
                                                    text: `${roleName} users are not vertical specific. Vertical and Location cleared.`,
                                                    confirmButtonColor: "#3085d6",
                                                });
                                            }
                                        }}


                                        placeholder="Select Role"
                                        classNamePrefix="react-select"
                                    />


                                    {errors.roles && (
                                        <small className="text-danger">{errors.roles}</small>
                                    )}



                                    {errors.role && (
                                        <small className="text-danger">{errors.role}</small>
                                    )}
                                </div>


                                <div className="col-md-6">
                                    <Select
                                        options={designationOptions}
                                        value={selectedDesignationOption}
                                        onChange={(selected) =>
                                            setFormData(prev => ({
                                                ...prev,
                                                designation: selected?.value || null,
                                            }))
                                        }
                                        placeholder="Select Designation"
                                        classNamePrefix="react-select"
                                    />


                                    {errors.designation && (
                                        <small className="text-danger">{errors.designation}</small>
                                    )}
                                </div>


                                {!isAdmin && (
                                    <div className="col-md-6">
                                        <Select
                                            options={managerOptions}
                                            value={selectedReportingManagerOption}
                                            onChange={(selected) =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    reportingManager: selected?.value || null,
                                                }))
                                            }
                                            placeholder="Select Reporting Manager"
                                            classNamePrefix="react-select"
                                            isClearable
                                        />
                                        {errors.reportingManager && (
                                            <small className="text-danger">{errors.reportingManager}</small>
                                        )}
                                    </div>
                                )}
                                <div className="col-md-6">
                                    <div className="form-floating">
                                        <input
                                            type="number"
                                            className={`form-control ${errors.salesTarget ? "is-invalid" : ""}`}
                                            name="salesTarget"
                                            placeholder="Sales Target"
                                            value={formData.salesTarget}
                                            onChange={handleChange}
                                        />
                                        <label>Sales Target (₹)</label>

                                        {errors.salesTarget && (
                                            <div className="invalid-feedback">{errors.salesTarget}</div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        )}

                        {/* ================= STEP 2 – LOGIN DETAILS ================= */}
                        {activeStep === 2 && (
                            <div className="row g-4">

                                {/* Username */}
                                <div className="col-md-4">
                                    <input
                                        className={`form-control ${errors.username ? "is-invalid" : ""}`}
                                        name="username"
                                        placeholder="Username *"
                                        value={formData.username}
                                        onChange={handleChange}
                                    />
                                    {errors.username && (
                                        <div className="invalid-feedback">{errors.username}</div>
                                    )}
                                </div>

                                {/* Password */}
                                <div className="col-md-4">
                                    <input
                                        type="password"
                                        className={`form-control ${errors.password ? "is-invalid" : ""}`}
                                        name="password"
                                        placeholder="Password *"
                                        value={formData.password}
                                        onChange={handleChange}
                                    />
                                    {errors.password && (
                                        <div className="invalid-feedback">{errors.password}</div>
                                    )}
                                </div>

                                {/* Confirm Password */}
                                <div className="col-md-4">
                                    <input
                                        type="password"
                                        className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                                        name="confirmPassword"
                                        placeholder="Confirm Password *"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                    />
                                    {errors.confirmPassword && (
                                        <div className="invalid-feedback">{errors.confirmPassword}</div>
                                    )}
                                </div>
                                <small className="text-muted">
                                    Leave password blank if you do not want to change it.
                                </small>

                            </div>
                        )}




                        {/* Footer */}
                        <div className="form-footer d-flex justify-content-end mt-4 gap-2">
                            {activeStep === 0 ? (
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate("/hr/users")}
                                >
                                    Cancel
                                </button>
                            ) : (
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => setActiveStep(s => s - 1)}
                                >
                                    Back
                                </button>
                            )}

                            {activeStep < 2 ? (
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setActiveStep(s => s + 1)}
                                >
                                    Next
                                </button>
                            ) : (
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                >
                                    {submitting ? "Updating..." : "Update Employee"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditHRUser;
